import { TCanvas } from './types';

/**
 * Will compute a ratio to adapt the font-size of the tooltip
 *
 * Case 1: the canvas is 500x200px, the image is 2400x800px, which makes a ratio of 2.5 for the
 * canvas and 3 for the image. As a result, the image will have margins on top and bottom because
 * it's reduced to fit the canvas.
 * Considering the canvas has a viewbox set to the width and height of the image, and that the
 * font-size is calculated relatively to this viewbox, we need to increase it inversely
 * proportionally to the ratio used to reduce it.
 *
 * If the image has been increased, then we return a ratio of 1 because we don't want to increase
 * the font-size.
 */
export const computeRatio = (
  imgRect?: {
    naturalWidth: number;
    naturalHeight: number;
  },
  canvas?: TCanvas
): number | undefined => {
  if (!(canvas && imgRect)) {
    return;
  }

  let width: number | undefined;
  let height: number | undefined;

  if ('clientWidth' in canvas && 'clientHeight' in canvas) {
    width = canvas.clientWidth;
    height = canvas.clientHeight;
  } else if ('width' in canvas && 'height' in canvas) {
    width = canvas.width;
    height = canvas.height;
  } else {
    console.warn('Unable to compute ratio: canvas dimensions are not available');
    return;
  }

  // container where the svg will be drawn
  const canvasRect = {
    width,
    height,
  };
  const imgRatio = imgRect.naturalWidth / imgRect.naturalHeight;
  const canvasRatio = canvasRect.width / canvasRect.height;

  if (imgRatio > canvasRatio) {
    if (imgRect.naturalHeight < canvasRect.height) {
      return 1;
    }

    return imgRect.naturalHeight / canvasRect.height;
  } else {
    if (imgRect.naturalWidth < canvasRect.width) {
      return 1;
    }

    return imgRect.naturalWidth / canvasRect.width;
  }
};

/**
 * Returns the id of the picto under the given event target, or null when the target is not inside a
 * picto (background image, empty space...). Pictos expose their id with a `data-picto-id` attribute.
 */
export const getPictoIdFromTarget = (target: EventTarget | null): number | null => {
  const pictoId = target instanceof Element ? target.closest('[data-picto-id]')?.getAttribute('data-picto-id') : null;

  return pictoId ? Number(pictoId) : null;
};
